import { Stack } from "@mui/system";
import { useState } from "react";
import { Container, Grid, SvgIcon, Typography } from "@mui/material";
import CustomAppBar from "../../../components/CustomAppBar";
import { useLocation, useNavigate } from "react-router";

import MenuBar from "../../menuBar";
import { useResponsive } from "../../../hooks/useResponsive";
import ExtendedReservationForm from "../../../components/forms/resservationForms/ExtendedReservationForm";
import moment from "moment-timezone";
import NoSpotReservationModal from "../../../components/reservation/Modals/NoSpotReservationModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCalculatedPricesRequest,
  availabilitySummaryRequest,
} from "../../../redux/reservations/action";

import CustomLoader from "../../../components/CustomLoader";
import {
  MOMENT_CONSTANTS,
  convertUtcToYard,
  getTimeZone,
} from "../../../utils/momentUtils";
import { ReactComponent as ReservationDateIcon } from "./../../../assets/svg/res_dateIcon.svg";
import { useTranslation } from "react-i18next";

const ExtendReservationPage = () => {
  const { isLoading } = useSelector((state) => state.reservationReducer);
  const navigate = useNavigate();
  const location = useLocation();
  const spotData = location?.state;
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  const fromDate = convertUtcToYard({
    date: spotData?.fromDate,
    timeZone: spotData?.timeZone,
    enableFormat: true,
  });
  const toDate = convertUtcToYard({
    date: spotData?.toDate,
    timeZone: spotData?.timeZone,
    enableFormat: true,
  });

  const lgUp = useResponsive("up", "lg");

  const todaysDate = moment(new Date())?.format(
    MOMENT_CONSTANTS.date.fullMonthDay,
  );
  const formSubmit = (values) => {
    const commonParams = {
      fromDate: moment(spotData?.toDate)?.format(
        MOMENT_CONSTANTS.dateWithTime.yearMonthDay24Hours,
      ),
      locationId: spotData?.spotAllocation?.[0]?.locationId,
      reservationType: "DAILY",
    };

    const availabilityParams = {
      ...commonParams,
      toDate: moment(commonParams.fromDate)
        .add(values?.numOfDays, "days")
        ?.format(MOMENT_CONSTANTS.dateWithTime.yearMonthDay24Hours),
    };

    const getRequestLines = (availableSpots = []) =>
      spotData?.spotAllocation?.[0]?.spots?.map((spotInfo) => ({
        spotType: spotInfo?.spotType,
        spotQty: spotInfo?.spotNums?.length,
        availableSpots: availableSpots.find(
          (availableSpotInfo) =>
            availableSpotInfo.spotType === spotInfo?.spotType,
        )?.count,
      }));

    const getVehicleList = (pricingItems = [], availableSpots = []) =>
      spotData?.spotAllocation?.[0]?.spots?.map((spotInfo) => ({
        clusterType: spotInfo?.spotType,
        spotLimit: availableSpots.find(
          (availableSpotInfo) =>
            availableSpotInfo.spotType === spotInfo?.spotType,
        )?.count,
        price: pricingItems.find(
          (pricingInfo) => pricingInfo.spotType === spotInfo?.spotType,
        )?.unitPrice,
        selectedCount: spotInfo?.spotNums?.length,
        totalCost: pricingItems.find(
          (pricingInfo) => pricingInfo.spotType === spotInfo?.spotType,
        )?.priceAmt,
        reservationType: "DAILY",
      }));

    let pricingParams = {
      ...commonParams,
      noOfDays: values?.numOfDays,
    };

    dispatch(
      availabilitySummaryRequest(availabilityParams, (response) => {
        if (response?.availabilitySummary?.availabilitySummary) {
          pricingParams.calcRequestLines = getRequestLines(
            response?.availabilitySummary?.availabilitySummary,
          );
          dispatch(
            fetchCalculatedPricesRequest(pricingParams, (pricingResponse) => {
              navigate("/check_out_reservation", {
                state: {
                  isExtend: true,
                  reservationId: spotData?.reservationId,
                  totalTax: pricingResponse?.totalTaxAmt,
                  subTotalCost: Number(pricingResponse?.totalPriceAmt),
                  deposit: pricingResponse?.totalDepositAmt,
                  discount: pricingResponse?.totalDiscountAmt,
                  net: pricingResponse?.totalNetAmt,
                  vehicleList: getVehicleList(
                    pricingResponse?.calcResponseLines,
                    response?.availabilitySummary?.availabilitySummary,
                  ),
                  numOfDays: values?.numOfDays,
                  fromDate: spotData?.toDate,
                  locationId: spotData?.spotAllocation?.[0]?.locationId,
                  timeZone: spotData?.timeZone,
                  reservationType: "DAILY",
                },
              });
            }),
          );
        }
      }),
    );
  };

  return (
    <div
      style={{
        width: lgUp ? `calc(100% - ${240}px)` : "100%",
        marginLeft: lgUp ? "240px" : 0,
      }}
    >
      <CustomAppBar
        title={t("pages.extendReservation.pageHeading")}
        showBackButton={true}
        onBackButtonClick={() => {
          navigate(-1);
        }}
      />
      <MenuBar />
      <Container sx={{ position: "relative", height: "90vh" }}>
        <Grid container direction={"row"} sx={{ mt: 2 }}>
          <Typography variant="h5" color={"primary.main"}>
            {t("pages.extendReservation.text01")}
          </Typography>
        </Grid>
        <Stack sx={{ mt: 1 }} spacing={1}>
          <Typography variant="fontSize16" color="common.inputCustomLable">
            {`${t("pages.extendReservation.text02")} ${todaysDate}`}
          </Typography>
          <Typography
            variant="fontSize16"
            color="common.inputCustomLable"
            fontWeight={"bold"}
          >
            {`${t("pages.extendReservation.text03")} - ${
              spotData.reservationId
            }`}
          </Typography>
          <Grid container direction={"row"} sx={{ mt: 2 }}>
            <SvgIcon
              component={ReservationDateIcon}
              inheritViewBox
              sx={{ width: "20px", height: "20px", mr: 1 }}
            />
            <Typography variant="body2" color={"primary.secondary"}>
              {`${fromDate} to ${toDate} ${getTimeZone(spotData?.timeZone)}`}
            </Typography>
          </Grid>
          <ExtendedReservationForm
            handleSubmit={(data) => {
              formSubmit(data);
            }}
          />
        </Stack>
        <NoSpotReservationModal
          isOpen={isModalOpen}
          onSubmit={() => {
            setModalOpen(false);
          }}
          onCancel={() => {
            setModalOpen(false);
          }}
        />

        {isLoading && <CustomLoader />}
      </Container>
    </div>
  );
};

export default ExtendReservationPage;
